import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Clock, Loader2, RefreshCw, Grid, List } from 'lucide-react';
import ArticleModal from './ArticleModal';
import { fetchRSSAppFeed, formatDate, truncateText, stripHTML } from '../utils/rssParser';

/**
 * RSSFeedDisplay - Custom RSS feed component to replace RSSApp widget
 * Fetches RSS feed data and displays articles inline without redirects
 */
const RSSFeedDisplay = ({ 
  widgetId = '_pR8Eo8G0EoH0dxAJ',
  title = 'Latest Articles',
  maxArticles = 10,
  showImages = true,
  compact = false
}) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch RSS feed on mount
  useEffect(() => {
    loadRSSFeed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetId]);

  const loadRSSFeed = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchRSSAppFeed(widgetId);
      // Filter out items without valid data
      const validArticles = data.filter(item => item.title && item.link);
      setArticles(validArticles.slice(0, maxArticles));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load RSS feed:', err);
      setError('Unable to load articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleCloseModal = () => {
    setSelectedArticle(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading articles...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={loadRSSFeed}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (articles.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No articles available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="rss-feed-display">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">{title}</h3>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={loadRSSFeed}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Last updated: {formatDate(lastUpdated.toISOString())}
        </p>
      )}

      {/* Articles Grid/List */}
      <AnimatePresence mode="wait">
        {viewMode === 'grid' ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {articles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleArticleClick(article)}
                className="bg-gray-800/50 hover:bg-gray-800 rounded-xl overflow-hidden cursor-pointer transition-all hover:scale-[1.02] border border-gray-700 hover:border-purple-500/50"
              >
                {/* Image */}
                {showImages && article.image && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-4">
                  {/* Categories */}
                  {article.categories && article.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {article.categories.slice(0, 2).map((cat, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-purple-600/20 text-purple-300 text-xs rounded-full"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Title */}
                  <h4 className="font-semibold text-white mb-2 line-clamp-2 hover:text-purple-400 transition-colors">
                    {article.title}
                  </h4>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-sm mb-3 line-clamp-3">
                    {truncateText(stripHTML(article.description || article.content), 100)}
                  </p>
                  
                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{article.author}</span>
                    <span>{formatDate(article.pubDate)}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            {articles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleArticleClick(article)}
                className="flex gap-4 p-4 bg-gray-800/30 hover:bg-gray-800 rounded-lg cursor-pointer transition-all border border-gray-700/50 hover:border-purple-500/30"
              >
                {/* Thumbnail */}
                {showImages && article.image && (
                  <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  {/* Categories */}
                  {article.categories && article.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {article.categories.slice(0, 2).map((cat, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-purple-600/20 text-purple-300 text-xs rounded-full"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Title */}
                  <h4 className="font-semibold text-white mb-1 line-clamp-1 hover:text-purple-400 transition-colors">
                    {article.title}
                  </h4>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-sm line-clamp-2 mb-2">
                    {truncateText(stripHTML(article.description || article.content), 120)}
                  </p>
                  
                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{article.author}</span>
                    <span>•</span>
                    <span>{formatDate(article.pubDate)}</span>
                  </div>
                </div>
                
                <div className="flex-shrink-0 flex items-center">
                  <ExternalLink className="w-4 h-4 text-gray-500" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Article Modal */}
      <ArticleModal
        isOpen={!!selectedArticle}
        onClose={handleCloseModal}
        article={selectedArticle}
      />
    </div>
  );
};

export default RSSFeedDisplay;
